import { HTMLAttributes, ThHTMLAttributes, TdHTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

// Table Root Component
export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  stickyHeader?: boolean
}

export const Table = ({ stickyHeader: _stickyHeader = false, className = '', children, ...props }: TableProps) => {
  return (
    <div className="overflow-x-auto">
      <table
        className={cn("w-full text-sm", className)}
        {...props}
      >
        {children}
      </table>
    </div>
  )
}

// Table Header Component
export interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {}

export const TableHeader = ({ className = '', children, ...props }: TableHeaderProps) => {
  return (
    <thead
      className={cn("bg-muted", className)}
      {...props}
    >
      {children}
    </thead>
  )
}

// Table Body Component
export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {}

export const TableBody = ({ className = '', children, ...props }: TableBodyProps) => {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  )
}

// Table Row Component
export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  interactive?: boolean
}

export const TableRow = ({ interactive = false, className = '', children, ...props }: TableRowProps) => {
  return (
    <tr
      className={cn(
        "border-b border-border",
        interactive && "hover:bg-muted/50 cursor-pointer",
        className
      )}
      {...props}
    >
      {children}
    </tr>
  )
}

// Table Head Cell Component
export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean
  sorted?: 'asc' | 'desc' | null
}

export const TableHead = ({ 
  sortable = false, 
  sorted = null, 
  className = '', 
  children, 
  onClick,
  ...props 
}: TableHeadProps) => {
  return (
    <th
      className={cn(
        "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground",
        sortable && "cursor-pointer select-none hover:bg-muted/80",
        className
      )}
      onClick={sortable ? onClick : undefined}
      {...props}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortable && (
          <span className="text-muted-foreground/50">
            {sorted === 'asc' ? '↑' : sorted === 'desc' ? '↓' : '↕'}
          </span>
        )}
      </div>
    </th>
  )
}

// Table Data Cell Component
export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {}

export const TableCell = ({ className = '', children, ...props }: TableCellProps) => {
  return (
    <td
      className={cn("px-4 py-3 text-foreground", className)}
      {...props}
    >
      {children}
    </td>
  )
}

// Export as default object
const TableComponents = {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
}

export default TableComponents
